import type { SessionLine, SessionState } from './ExperienceSession';
import type { Beat, BeatKind } from './beats';

/**
 * If the server accepts the socket and never answers, the session phase never
 * reaches a releasing state. This deadline, timed from the first Enter,
 * releases the sting's hold anyway so it can never park forever.
 */
export const STING_HOLD_DEADLINE_MS = 10_000;

/** The single chip offered after the session has stopped. */
export const RETRY_CHIPS = ['Try again'];

/** Who the display credits a message from the client itself to. */
export const SYSTEM_SPEAKER = 'Mission Unpossible';

/**
 * The session phases that mean it has settled one way or another, so the
 * sting's hold can release: something is now on screen to land the ring on.
 */
export const STING_RELEASING_PHASES: Array<SessionState['phase']> = [
  'connected',
  'stopped',
  'offline',
  'reconnecting',
];

/** True once the session phase means the sting's hold should release. */
export function phaseReleasesHold(phase: SessionState['phase']): boolean {
  return STING_RELEASING_PHASES.includes(phase);
}

export type VoicePhase =
  | 'ready' | 'listening' | 'transcribing' | 'thinking' | 'speaking'
  | 'unsupported' | 'denied' | 'failed';

export interface VoiceView { phase: VoicePhase; message: string | null }

export const SPEAK_CHIP = 'Speak';
export const STOP_CHIP = 'Stop';

/**
 * The typed-reply chip (keyboard icon, accessible name "Type"). Standard HTML
 * inputs open the on-glasses composer (handwriting + voice) on focus and tap;
 * no SDK call is needed, but the composer "may be unavailable on some
 * builds", and microphone access for web apps is not shipped to every
 * program member, so a typed path has to exist whenever the mic does not
 * (R-DISP-01, R-EVID-01, MRBD 2026-08-24 webapp capability evidence).
 */
export const TYPE_CHIP = 'Type';

/** Player-facing voice copy, the phone client's words, verbatim. */
export const VOICE_COPY = {
  micPrePrompt: 'Speak needs the microphone. Pinch again to allow.',
  tooShort: 'That was too short to send. Try again.',
  captureFailed: 'Voice capture failed. You can keep typing.',
  playbackFailed: 'Could not play voice response. Text remains available.',
  sttAuth: 'Your session needs a refresh before voice can send. Sign in again if this persists; typed messages still work.',
  sttUnavailable: 'Voice transcription is not available right now. You can keep typing.',
  sttNetwork: 'Your voice could not reach the experience. Check your connection or keep typing.',
  sttMalformed: 'The experience could not read that recording. Try again or type instead.',
  sttUnknown: 'Voice input hit a problem. You can keep typing.',
} as const;

/** Everything the hud needs to draw itself, with no Phaser or React in it. */
export interface HudView {
  status: string;
  line: SessionLine | null;
  /**
   * 'active' when `line` is the step's live, unanswered question and should
   * stay on screen until the player answers it or something replaces it.
   * 'transient' when it is a passing cue that clears on its own (the toast
   * rule). See beats.ts, which is what a caller passing `beat` is normally
   * fed by.
   */
  lineKind: BeatKind;
  chipLabels: string[];
  chipsEnabled: boolean;
  /** The Speak/Stop chip stays pressable while the others are disabled mid-turn. */
  micChipEnabled: boolean;
}

/**
 * Turns one session state into one screenful.
 *
 * This is the whole state to screen rule in one place, so it can be read and
 * tested without rendering anything. It is the same mapping both glasses
 * clients use, so they behave alike.
 *
 * `beat`, when passed, is the presentation queue's current beat (beats.ts):
 * it overrides `line`/`lineKind` so only one queued cue shows at a time,
 * with nothing shown when the queue is empty. Callers that don't pass it
 * (or pass it as stopped) keep the session's raw `state.line`, so existing
 * callers are unaffected.
 */
export function toHudView(
  state: SessionState,
  voice?: VoiceView,
  beat?: Beat | null,
): HudView {
  const stopped = state.phase === 'stopped';
  const talkEnabled = stopped || (state.phase === 'connected' && !state.turnInFlight);
  if (stopped || !voice || voice.phase === 'unsupported') {
    const showType = !stopped && showTypeChip(state);
    const line = stopped ? stoppedLine(state.stoppedReason) : lineFor(state, beat);
    return {
      status: state.lastError ?? statusFor(state),
      line,
      lineKind: stopped ? 'transient' : lineKindFor(state, beat),
      chipLabels: stopped ? RETRY_CHIPS : showType ? [TYPE_CHIP] : [],
      chipsEnabled: talkEnabled,
      micChipEnabled: false,
    };
  }
  const busy = voice.phase === 'listening' || voice.phase === 'transcribing' || voice.phase === 'thinking';

  // The player can always talk to Throckmorton while connected (Jason,
  // 2026-09-29: "a player should be able to talk to throck when they want").
  // The mic and keyboard never hide for the step's answer data: talking is
  // how a player chats, asks, or says they are done (there is no exit
  // keyword). The step's answer data only decides extras such as choices.
  const showType = showTypeChip(state);
  const chipLabels: string[] = [];
  chipLabels.push(voice.phase === 'listening' ? STOP_CHIP : SPEAK_CHIP);
  if (showType) chipLabels.push(TYPE_CHIP);

  return {
    status:
      voice.message ??
      state.lastError ??
      voiceStatusFor(voice.phase) ??
      statusFor(state),
    line: lineFor(state, beat),
    lineKind: lineKindFor(state, beat),
    chipLabels,
    chipsEnabled: talkEnabled && !busy,
    micChipEnabled: state.phase === 'connected' && voice.phase !== 'transcribing' && voice.phase !== 'thinking',
  };
}

/** The Type chip (typed words, one of the answer forms) shows whenever the session is connected. */
function showTypeChip(state: SessionState): boolean {
  return state.phase === 'connected';
}

/** The line to show: the presentation queue's current beat when given, else the session's raw line. */
function lineFor(state: SessionState, beat?: Beat | null): SessionLine | null {
  if (beat !== undefined) return beat ? { ...(beat.line ?? { speaker: state.line?.speaker ?? SYSTEM_SPEAKER }), text: beat.text } : null;
  return state.line;
}

/**
 * The shown line's kind. With a beat, this is exactly what the queue says.
 * Without one (a caller not yet using the presentation queue), this falls
 * back to the same source.response.awaiting test the queue itself uses, so
 * legacy callers get a reasonable default rather than always 'transient'.
 */
function lineKindFor(state: SessionState, beat?: Beat | null): BeatKind {
  if (beat !== undefined) return beat?.kind ?? 'transient';
  return state.line?.source?.response?.awaiting ? 'active' : 'transient';
}

function voiceStatusFor(phase: VoicePhase): string | null {
  switch (phase) {
    case 'listening': return 'listening';
    case 'transcribing': return 'sending';
    case 'thinking': return 'sending';
    case 'denied': return 'microphone off';
    default: return null;
  }
}

function statusFor(state: SessionState): string {
  switch (state.phase) {
    case 'idle':
      return 'press Enter';
    case 'connecting':
      return 'connecting';
    case 'connected':
      if (state.turnInFlight) return 'sending';
      return state.mission?.phase ?? 'connected';
    case 'reconnecting':
      return 'reconnecting';
    case 'offline':
      return 'offline';
    case 'stopped':
      return 'stopped';
  }
}

/** The session records which step failed as a short tag. This says it out loud. */
function stoppedLine(reason: string | null): SessionLine {
  return { speaker: SYSTEM_SPEAKER, text: stoppedText(reason) };
}

function stoppedText(reason: string | null): string {
  switch (reason) {
    case null:
      return 'The session stopped.';
    case 'sign-in':
      return 'Could not sign in.';
    case 'experience discovery':
      return 'Could not find the experience.';
    case 'connection':
      return 'Could not reach the experience.';
    case 'sign-in renewal':
      return 'The sign-in expired and could not be renewed.';
    default:
      return `Stopped at ${reason}.`;
  }
}
